import {ChangeDetectorRef, Component, EventEmitter, Input, NgZone, OnInit, Output, ViewChild} from '@angular/core';
import {AuthoringManagerService} from "../../services/authoring-manager.service";
import {RepositoryService} from "../../services/repository.service";
import {Assistance, AssistanceType} from "../../models/assistance";
import {ResourceType, ResourceUtils} from "../../models/resource";
import {
  ChooseResourceDialogComponent,
  ChooseResourceDialogData
} from "../dialogs/choose-resource-dialog/choose-resource-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {Equipment} from "../../models/equipment";
import {UiManagerService} from "../../services/ui-manager.service";
import {AccountManagerService} from "../../services/account-manager-service";
import {DeviceType, VideocallProvider, VideocallStatus} from "../../models/videocall/videocall-status";
import {
  EditorArrowTool,
  EditorMeasureTool,
  EditorShapeTool,
  EditorTextSizeScalingFactor,
  EditorTool
} from "../../models/editor-tools";
import {EditorToolsStatus} from "./editor-tools/editor-tools.component";
import {Utils} from "../../utils";
import {DEBUG, DebugService} from "../../services/debug.service";
import {TranslatePipe} from "@ngx-translate/core";
import {ScriptService} from "../../services/scripts/script-service";
import {environment} from "@environments/environment";
import {MaintenanceManagerService} from "../../services/maintenance-manager.service";
import {VideocallUnityComponent} from "./videocall-providers/videocall-unity/videocall-unity.component";
import {VideocallBabylonComponent} from "./videocall-providers/videocall-babylon/videocall-babylon.component";
import {IVideocallConfig} from "../../models/videocall/i-videocall-confi";
import {SettingsManagerService} from "../../services/settings-manager.service";
import {VideocallMessageType} from "../../models/videocall/videocall-message-type";

enum FileType {
  IMAGE, PDF, MODEL
}

enum RecordingStatus {
  READY,
  STARTING,
  RECORDING,
  STOPPING,
}

@Component({
  selector: 'app-videocall',
  templateUrl: './videocall.component.html',
  styleUrls: ['./videocall.component.scss']
})
export class VideocallComponent implements OnInit {

  @Input()
  companyId: string;

  @Input()
  equipment: Equipment;

  @Input()
  assistance: Assistance;

  @Input()
  assistanceType: AssistanceType;

  @Input()
  info: string;

  @Input()
  videoCallStarted: boolean;

  @Input()
  canEditScene: boolean;

  @Input()
  canRecord: boolean;

  @Output()
  onPreparationCompleted = new EventEmitter();

  @Output()
  onVideocallClosed = new EventEmitter();

  /* Core */
  videocallBackend: 'Unity' | 'Babylon';

  /* Controllers */
  freezed: boolean;
  isMuted: boolean;
  isFullscreen: boolean;

  /* Editor */
  editorTool: EditorTool;
  editorShown: boolean;
  currEditorStatus: EditorToolsStatus;

  /* Videocall status */
  recordingStatus: RecordingStatus = RecordingStatus.READY;
  videocallInfo = this.translatePipe.transform('Waiting for response...');
  isVideocallCompleted = false;
  hasRecorded: boolean;

  /* Videocall provider components */
  @ViewChild(VideocallUnityComponent, { static: false }) videocallUnity: VideocallUnityComponent;
  @ViewChild(VideocallBabylonComponent, { static: false }) videocallBabylon: VideocallBabylonComponent;
  get currVideocallComponent(): VideocallUnityComponent | VideocallBabylonComponent {
    return this.videocallBackend == 'Unity' ? this.videocallUnity : this.videocallBabylon;
  }

  /* Utils */
  recoveryMessageTimeout: any;
  recoveryFailedTimeout: any;
  stream: MediaStream;

  constructor(
    private accountManagerService: AccountManagerService,
    private authoringManagerService: AuthoringManagerService,
    private maintenanceManager: MaintenanceManagerService,
    private repositoryService: RepositoryService,
    private settingsManagerService: SettingsManagerService,
    private scriptService: ScriptService,
    private debugService: DebugService,
    private uiManagerService: UiManagerService,
    private dialog: MatDialog,
    private translatePipe: TranslatePipe,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) { }

  ngOnInit(): void {
  }

  private checkBackend(backend: 'Unity' | 'Babylon'): boolean {
    // Check videocall module backend
    switch (backend) {
      case 'Unity':
        this.debugService.log(DEBUG.VIDEOCALL, 'Using Unity videocall backend');
        break;
      case 'Babylon':
        this.debugService.log(DEBUG.VIDEOCALL, 'Using Babylon videocall backend');
        break
      default:
        this.debugService.error(DEBUG.VIDEOCALL, `Unknown videocall backend: ${this.videocallBackend}`);
        return false;
    }
    return true;
  }

  async prepareVideocall(backend: 'Unity' | 'Babylon') {
    if (!this.checkBackend(backend)) return;
    this.videocallBackend = backend;
    this.updateUI();
    await this.currVideocallComponent.prepareVideocall();
    await this.requestMediaStreamDevices();
  }

  async requestMediaStreamDevices() {

    // Check permissions
    while (this.stream == null || this.stream.active == null) {

      try {
        // TODO check if we can avoid to capture video stream -> useless in this context (expert POV)
        this.stream = await navigator.mediaDevices.getUserMedia({audio: true, video: false});
      } catch (e) {
        switch (e.message) {
          case 'Requested device not found':
            this.videocallInfo = this.translatePipe.transform('Connect the webcam and give permissions');
            break;
          case 'Permission denied':
            this.videocallInfo = this.translatePipe.transform('No camera permission. Try again!');
            break;
        }
      }

      await Utils.sleep(1000);

    }

    this.videocallInfo = this.translatePipe.transform('Waiting for response...');
    this.onPreparationCompleted.emit();

    this.updateUI();

  }

  async startVideocall() {
    await this.currVideocallComponent.startVideocall();
  }

  async onMessageFromModule(data: { eventType: VideocallMessageType, data?: string }) {

    switch (data.eventType) {
      case VideocallMessageType.MODULE_LOADED:
        this.onModuleLoaded();
        break;
      case VideocallMessageType.MODULE_SCENE_LOADED:
        this.onModuleSceneLoaded();
        break;
      case VideocallMessageType.CALL_STARTED:
        this.onCallStarted();
        break;
      case VideocallMessageType.CALL_NEW_CONNECTION:
        this.onCallNewConnection();
        break;
      case VideocallMessageType.CALL_DISCONNECTED:
        this.onCallClosed();
        break;
      case VideocallMessageType.CALL_RECORD_STARTED:
        this.onRecordStarted();
        break;
      case VideocallMessageType.CALL_RECORD_STOPPED:
        this.onRecordStopped();
        break;
      case VideocallMessageType.ANNOTATION_TEXT:
        this.onAnnotationText();
        break;
      case VideocallMessageType.CALL_CLOSED:
        this.onCallClosed();
        break;
      case VideocallMessageType.MODULE_UNLOADED:
        this.onModuleUnloaded();
        break;
    }

    this.updateUI();

  }

  onModuleLoaded() {
    if (!this.isVideocallCompleted) {
      this.currVideocallComponent.goToExpert();
    }
  }

  onModuleSceneLoaded() {
    this.maintenanceManager.waitForDeviceToBeReady(this.assistance, true, async (deviceType: DeviceType, videocallProvider: VideocallProvider) => {

      const config = {
        signaller: videocallProvider == VideocallProvider.BYN ? `${(await this.settingsManagerService.getSignallers()).byn}/conferenceapp` : (await this.settingsManagerService.getSignallers()).jitsi,
        room_name: this.assistance.id,
        node_type: 'server', // Solo per byn
        call_type: 'conference', // Solo per byn
        resolution: 'med',
        forceTwilio: videocallProvider == VideocallProvider.BYN, // Solo per byn
        remote_device_type: VideocallStatus.DeviceTypeAsString(deviceType),
        webrtc_provider: VideocallStatus.VideocallProviderAsString(videocallProvider),
        nick_name: 'Expert'
      } as IVideocallConfig;

      if (!environment.production) {
        this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'Videocall config', config);
      }

      this.currVideocallComponent.startScotty(config);

    });
  }

  onCallStarted() {
    this.videocallInfo = '';
    this.videoCallStarted = true;
  }

  onCallNewConnection() {
    this.videocallInfo = '';
    if (this.recoveryMessageTimeout) clearTimeout(this.recoveryMessageTimeout);
    if (this.recoveryFailedTimeout) clearTimeout(this.recoveryFailedTimeout);
  }

  onDisconnected() {
    if (!this.isVideocallCompleted) {
      this.videocallRecovery();
    }
  }

  onRecordStarted() {
    this.recordingStatus = RecordingStatus.RECORDING;
    this.hasRecorded = true;
    this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'Recording is started');
  }

  onRecordStopped() {
    this.recordingStatus = RecordingStatus.READY;
    this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'Recording is stopped');
  }

  onAnnotationText() {
    // A text field was added in scene -> input text
    this.completeTextInput();
  }

  onCallClosed() {
    this.stopCall();
  }

  onModuleUnloaded() {
    /*this.isVideocallStarted = false;
    this.isVideocallCompleted = true;*/
  }

  // Try to recover videocall due to disconnection event
  videocallRecovery() {
    this.recoveryMessageTimeout = setTimeout(() => {
      this.videocallInfo = this.translatePipe.transform('Trying to reconnect...');
    }, 5000);
    this.recoveryFailedTimeout = setTimeout(() => {
      this.stopCall();
    }, 60000);
  }

  async stopCall() {

    // TODO is this flag correct?
    // Previous check: !this.unityInstance
    if (!this.videoCallStarted) {
      return;
    }

    //await this.queueManagerService.setWebReady(this.currAppointment.id.toString(), false);

    // Stop recording
    this.stopRecording(false);
    await Utils.sleep(200); // Give time to signal to propagate

    // Stop Unity module
    this.currVideocallComponent.closeCall();
    // setTimeout(() => this.unityInstance.SendMessage("ExternalCommunicator", "Quit"), 1000);
    setTimeout(() => this.currVideocallComponent.unityQuit(), 1000);

    // Stop local tracks
    this.stream.getTracks().forEach(track => track.stop());

    // Reset flags
    this.isVideocallCompleted = true;
    this.videoCallStarted = false;
    this.onVideocallClosed.emit();
  }

  goFullscreen() {
    this.isFullscreen = !this.isFullscreen;
    if (this.isFullscreen) {
      this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'Going fullscreen...');
      this.uiManagerService.hideSidebar();
    } else {
      this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'Exiting fullscreen...');
      this.uiManagerService.showSidebar();
    }
    // TODO update useless?
    this.updateUI();
    this.currVideocallComponent.forceResize();
  }

  freeze() {
    this.freezed = true;
    this.currVideocallComponent.freeze();
  }

  unfreeze() {
    this.freezed = false;
    this.editorTool = null;
    this.editorShown = false;
    this.currVideocallComponent.unfreeze();
  }

  arrow() {
    this.freeze();
    this.showEditor(EditorTool.ARROW);
  }

  shape() {
    this.freeze();
    this.showEditor(EditorTool.SHAPE);
  }

  measure() {
    this.freeze();
    this.showEditor(EditorTool.MEASURE);
  }

  freedraw() {
    this.freeze();
    this.showEditor(EditorTool.FREEHAND);
  }

  selectText() {
    this.freeze();
    this.showEditor(EditorTool.TEXT);
  }

  completeTextInput() {
    this.uiManagerService.showTextInputDialog('Input box', 'Value', (value) => {
      this.currVideocallComponent.enrichText(`${value.replace(/[,&]/g, ' ')},${(this.currEditorStatus.selectedSubtool + 1) * EditorTextSizeScalingFactor},${Utils.hexToRgbString(this.currEditorStatus.selectedColor)}`);
    }, () => {
      this.currVideocallComponent.cancelText();
    });
  }

  showEditor(tool: EditorTool) {
    this.editorTool = tool;
    this.editorShown = true;
  }

  onEditorUpdate(status: EditorToolsStatus) {

    switch (status.selectedTool) {

      // Arrow tool was selected
      case EditorTool.ARROW: {

        let rotation = 0;
        let flip = 'none';
        let type = 'normal';
        switch (status.selectedSubtool as EditorArrowTool) {
          case EditorArrowTool.ARROW_LEFT:
            /* Nothing */
            break;
          case EditorArrowTool.ARROW_UP:
            rotation = 270;
            break;
          case EditorArrowTool.ARROW_RIGHT:
            rotation = 180;
            break;
          case EditorArrowTool.ARROW_BOTTOM:
            rotation = 90;
            break;
          case EditorArrowTool.ARROW_CLOCKWISE:
            flip = 'h';
            type = 'curved_180';
            break;
          case EditorArrowTool.ARROW_ANTICLOCKWISE:
            type = 'curved_180';
            break;
        }

        this.currVideocallComponent.selectArrow(`${Utils.hexToRgbString(status.selectedColor)},${type},${rotation},${flip}`);
        break;
      }

      // Shape tool was selected
      case EditorTool.SHAPE: {

        // Hide measurements in module
        this.currVideocallComponent.hideMeasurements();

        let shape = '';
        switch (status.selectedSubtool as EditorShapeTool) {
          case EditorShapeTool.SHAPE_CIRCLE:
            shape = 'circle';
            break;
          case EditorShapeTool.SHAPE_RECTANGLE:
            shape = 'rectangle';
            break;
          case EditorShapeTool.SHAPE_LINE:
            shape = 'line';
            break;
        }

        this.currVideocallComponent.selectShape(`${Utils.hexToRgbString(status.selectedColor)},${shape},${status.selectedWidth + 1}`);
        break;
      }

      // Measure tool was selected
      case EditorTool.MEASURE: {

        // Show measurements in module
        this.currVideocallComponent.showMeasurements();

        let shape = '';
        switch (status.selectedSubtool as EditorMeasureTool) {
          case EditorMeasureTool.MEASURE_CIRCLE:
            shape = 'circle';
            break;
          case EditorMeasureTool.MEASURE_RECTANGLE:
            shape = 'rectangle';
            break;
          case EditorMeasureTool.MEASURE_LINE:
            shape = 'line';
            break;
        }

        this.currVideocallComponent.selectShape(`${Utils.hexToRgbString(status.selectedColor)},${shape},${status.selectedWidth + 1}`);
        break;
      }

      // Freedraw tool was selected
      case EditorTool.FREEHAND: {
        this.currVideocallComponent.selectFreedraw(`${Utils.hexToRgbString(status.selectedColor)},${status.selectedWidth + 1}`);
        break;
      }

      // Text tool was selected
      case EditorTool.TEXT: {

        // Save editor status for text input properties
        this.currEditorStatus = status;

        this.currVideocallComponent.selectText();
        break;
      }

      default:
        this.debugService.error(DEBUG.VIDEOCALL, `Unknown editor subtool: ${status.selectedSubtool}`);
        break;
    }

  }

  clearDrawings() {
    this.currVideocallComponent.clear();
  }

  mute() {
    this.isMuted = !this.isMuted;
    this.setMute(this.isMuted);
    this.updateUI();
  }

  setMute(muted: boolean) {
    this.currVideocallComponent.setMute(muted);
  }

  addImage() {
    this.selectFile(FileType.IMAGE, async (file: File) => {
      const url = await this.authoringManagerService.uploadVideocallContent(this.assistance, ResourceType.IMAGE, file);
      this.sendImageToUnity(url);
    });
  }

  addModel() {
    this.selectFile(FileType.MODEL, async (file: File) => {
      const url = await this.authoringManagerService.uploadVideocallContent(this.assistance, ResourceType.MODEL, file);
      this.sendModelToUnity(url);
    });
  }

  addPdf() {
    this.selectFile(FileType.PDF, async (file: File) => {
      const url = await this.authoringManagerService.uploadVideocallContent(this.assistance, ResourceType.DOC, file);
      this.sendPDFToUnity(url);
    });
  }

  async addFromLibrary() {

    const dialogRef = this.dialog.open(ChooseResourceDialogComponent, {
      width: '1200px',
      data: {equipment: this.equipment, rows: 2, types: [ResourceType.DOC]} as ChooseResourceDialogData,
    });
    const result: ChooseResourceDialogData = await dialogRef.afterClosed().toPromise();

    if (result) {

      // Perform bind between resource and maintenance
      this.authoringManagerService.bindResourceToMaintenance(this.assistance, result.resource);

      switch (result.resource.type) {
        case ResourceType.IMAGE:
          this.sendImageToUnity(result.resource.url);
          break;
        case ResourceType.MODEL:
          this.sendModelToUnity(result.resource.url);
          break;
        case ResourceType.DOC:
          this.sendPDFToUnity(result.resource.url);
          break;
      }
    }

    this.updateUI();

  }

  takeScreenshot() {
    this.currVideocallComponent.takeScreenshot();
  }

  sendImageToUnity(url: string) {
    this.currVideocallComponent.addImage(url);
    this.uiManagerService.notify('Image uploaded successfully');

    // Used to clear elements on screen
    setTimeout(() => {
      this.unfreeze();
    }, 1000);
  }

  sendModelToUnity(url: string) {
    this.currVideocallComponent.addModel(url);
    this.uiManagerService.notify('Model uploaded successfully');

    // Used to clear elements on screen
    setTimeout(() => {
      this.unfreeze();
    }, 1000);
  }

  sendPDFToUnity(url: string) {
    this.currVideocallComponent.addPdf(url);
    this.uiManagerService.notify('PDF uploaded successfully');

    // Used to clear elements on screen
    setTimeout(() => {
      this.unfreeze();
    }, 1000);
  }

  selectFile(fileType: FileType, callback: (file: File) => void) {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';

    switch (fileType) {
      case FileType.IMAGE:
        fileInput.accept = ResourceUtils.ResourceTypesToContentType([ResourceType.IMAGE]);
        break;
      case FileType.MODEL:
        fileInput.accept = ResourceUtils.ResourceTypesToContentType([ResourceType.MODEL]).replace('model/gltf-binary', '.gltf,.glb'); // Fix required to get right files
        break;
      case FileType.PDF:
        fileInput.accept = ResourceUtils.ResourceTypesToContentType([ResourceType.DOC]);
        break;
    }

    fileInput.onchange = e => {
      callback(e.target['files'][0]);
    }
    fileInput.click();
  }

  async startRecording() {
    if (this.hasRecorded) {
      this.uiManagerService.showConfirmDialog(
        'Record maintenance',
        'This maintenance has already been recorder. Do you want to overwrite the recording?',
        () => {
          this.doStartRecording();
          this.updateUI();
        }
      )
    } else {
      await this.doStartRecording();
    }
  }

  async doStartRecording() {

    // Exit if not in ready state
    if (this.recordingStatus != RecordingStatus.READY) {
      this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'Not in ready state to start recording...');
      return;
    }

    this.recordingStatus = RecordingStatus.STARTING;
    this.debugService.log(DEBUG.LIVE_ASSISTANCE, `Record is starting with room ${this.assistance.id}...`);

    const startRecordingJson = await this.getStartRecordingJson();
    this.currVideocallComponent.directMessage(`record=${startRecordingJson}`);
    this.currVideocallComponent.notifyRecording(startRecordingJson);
  }

  stopRecording(showDialog: boolean) {

    // Exit if not recording anything
    if (this.recordingStatus != RecordingStatus.RECORDING) {
      this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'Not in recording state to stop recording...');
      return;
    }

    // Stop recording action
    const stopRecording = () => {
      this.recordingStatus = RecordingStatus.STOPPING;
      this.debugService.log(DEBUG.LIVE_ASSISTANCE, `Record is stopping with room ${this.assistance.id}...`);

      const stopRecordingJson = this.getStopRecordingJson();
      this.currVideocallComponent.directMessage(`record=${stopRecordingJson}`);
      this.currVideocallComponent.notifyRecording(stopRecordingJson);
    }

    if (showDialog) {
      this.uiManagerService.showConfirmDialog(
        'Stop recording',
        'Do you want to stop the recording',
        () => {
          stopRecording();
          this.updateUI();
        }
      );
    } else {
      stopRecording();
    }

  }

  async getStartRecordingJson() {
    return JSON.stringify({evt: "record", conference: this.assistance.id, action: "start", destination: {
        companyId: this.companyId,
        equipmentId: this.equipment.id,
        maintenanceId: this.assistance.maintenance.id, // TODO change this.assistance.maintenance.id to this.assistance.id
        pin: await this.accountManagerService.requestPIN()
      }});
  }

  getStopRecordingJson() {
    return JSON.stringify({evt: "record", conference: this.assistance.id, action: "stop"});
  }

  updateUI() {
    this.cdr.detectChanges();
  }

  get RecordingStatus() { return RecordingStatus; }

  get AssistanceType() { return AssistanceType }

  get environment() { return environment; }

}
